// packages/web-ui/src/panels/views/ConnectionEdge.tsx
//
// W4-S1 — Custom edge type derived from the inline edge config in
// `tools/view-renderer/reactflow-app/src/App.jsx` lines 75–88. Smoothstep
// path, ArrowClosed marker, optional label with a per-`kind` legend icon
// (UX §6.4.3 lines 829: ⚡ 💧 📡 🔗).

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  useReactFlow,
  type EdgeProps,
} from '@xyflow/react';
import { FLOW_COLORS } from '@tessa-lean/base';
import type { ConnectionEdgeData, ConnectionKind } from './layout-builder.js';

/**
 * Callback shape used by ConnectionEdge to push waypoint updates back up to
 * the parent (ViewRenderer). Stuffed into each edge's `data` so the edge
 * renderer can dispatch updates without a context. `waypoints` is the full
 * new list; an empty array clears all bends and reverts to auto-routing.
 */
export type EdgeWaypointHandler = (
  edgeId: string,
  waypoints: ReadonlyArray<{ x: number; y: number }>,
) => void;

export const KIND_ICON: Record<ConnectionKind, string> = {
  energy: '⚡',
  material: '💧',
  mechanical: '⚙',
  information: '📡',
  reference: '🔗',
};

/**
 * Per-kind stroke style. The canonical source of truth for both
 * `<ConnectionEdge>` and `<EdgeLegend>` — change one, change both.
 */
export interface EdgeKindStyle {
  /** SVG stroke colour. */
  stroke: string;
  /** Stroke width in CSS pixels. */
  strokeWidth: number;
  /** SVG `stroke-dasharray` string; omit for a solid stroke. */
  strokeDasharray?: string;
  /** Human-readable label for the legend. */
  label: string;
}

// Stroke colours are sourced from the SHARED `@tessa-lean/base` flow-colour
// palette (the single source of truth) so the React Flow connectors and the
// server SVG export can never drift. The widths / dash patterns / legend
// labels remain web-ui presentation concerns owned here.
export const EDGE_KIND_STYLES: Record<ConnectionKind, EdgeKindStyle> = {
  energy: {
    stroke: FLOW_COLORS.energy, // amber-600 — power / electricity
    strokeWidth: 2.8, // thickest solid stroke — the dominant power flow
    label: 'Energy flow',
  },
  mechanical: {
    stroke: FLOW_COLORS.mechanical, // dark brown / rust — rigid mechanical / torque
    strokeWidth: 2.6,
    label: 'Mechanical flow',
  },
  information: {
    stroke: FLOW_COLORS.information, // blue-600 — signals / data
    strokeWidth: 1.8,
    strokeDasharray: '1.5 5', // fine dotted (round linecap → crisp dots)
    label: 'Information flow',
  },
  material: {
    stroke: FLOW_COLORS.material, // cyan-600 — fluid / material flow
    strokeWidth: 2.2,
    strokeDasharray: '9 5', // even dashes
    label: 'Material flow',
  },
  reference: {
    stroke: FLOW_COLORS.reference, // neutral-500 — references
    strokeWidth: 1.4,
    strokeDasharray: '2 4',
    label: 'Reference',
  },
};

const DEFAULT_EDGE_STYLE: EdgeKindStyle = {
  stroke: 'var(--tessa-edge)',
  strokeWidth: 1.2,
  label: 'Connection',
};

/**
 * Per-`changeKind` override applied when the edge carries a
 * `data.changeKind` tag from a changeset-preview synthesis. The colour
 * overrides the per-flow-kind stroke; removed edges are dashed.
 */
type ChangeKindEdgeStyle = {
  stroke: string;
  strokeWidth: number;
  strokeDasharray?: string;
};

export const CHANGE_KIND_EDGE_STYLES: Record<
  'added' | 'updated' | 'removed',
  ChangeKindEdgeStyle
> = {
  added: { stroke: '#10B981', strokeWidth: 2.4 }, // emerald-500
  updated: { stroke: '#F59E0B', strokeWidth: 2.2 }, // amber-500
  removed: {
    stroke: '#F43F5E', // rose-500
    strokeWidth: 2.0,
    strokeDasharray: '6 4',
  },
};

/**
 * Pure label content used by `<ConnectionEdge>` and exercised directly in
 * tests to avoid the React Flow viewport/portal dance.
 */
export function ConnectionEdgeLabel(props: {
  edgeId: string;
  data: ConnectionEdgeData;
}): JSX.Element | null {
  const { edgeId, data } = props;
  const kindIcon = data.kind !== undefined ? KIND_ICON[data.kind] : undefined;
  const labelText = data.label;
  // A coalesced connector stands in for N>1 KB connections sharing the exact
  // same port pair (see `coalesceOverlappingEdges`). Surface that as a compact
  // `×N` so the fold is visible, not silent.
  const mult =
    typeof data.multiplicity === 'number' && data.multiplicity > 1
      ? data.multiplicity
      : undefined;
  if (labelText === undefined && kindIcon === undefined && mult === undefined) {
    return null;
  }
  return (
    <div
      data-edge-id={edgeId}
      data-edge-kind={data.kind ?? ''}
      className="connection-edge-label"
    >
      {kindIcon !== undefined && (
        <span data-edge-icon style={{ marginRight: labelText !== undefined ? 4 : 0 }}>
          {kindIcon}
        </span>
      )}
      {labelText}
      {mult !== undefined && (
        <span
          data-edge-multiplicity={mult}
          title={`${mult} connections on this port pair`}
          style={{ marginLeft: labelText !== undefined || kindIcon !== undefined ? 4 : 0, opacity: 0.75 }}
        >
          ×{mult}
        </span>
      )}
    </div>
  );
}

const ORTHO_EPSILON = 0.5;

/** Corner-rounding radius (px) applied at each interior bend of a polyline. */
const CORNER_RADIUS = 9;

function approxEq(a: number, b: number): boolean {
  return Math.abs(a - b) < ORTHO_EPSILON;
}

/**
 * Build an SVG path through `points`, rounding each interior corner with a
 * short quadratic arc (radius clamped to half the shorter adjacent segment so
 * tight corners never overshoot). For an orthogonal polyline this yields the
 * gently-rounded right-angle connectors of a polished architecture diagram
 * while leaving the run of each segment dead straight. A 2-point list (no
 * bends) degrades to a plain `M … L …`.
 */
export function roundedPolylinePath(
  points: ReadonlyArray<{ x: number; y: number }>,
  radius: number,
): string {
  if (points.length < 2) return '';
  if (points.length === 2) {
    return `M ${points[0]!.x} ${points[0]!.y} L ${points[1]!.x} ${points[1]!.y}`;
  }
  let d = `M ${points[0]!.x} ${points[0]!.y}`;
  for (let i = 1; i < points.length - 1; i += 1) {
    const prev = points[i - 1]!;
    const cur = points[i]!;
    const next = points[i + 1]!;
    const lenIn = Math.hypot(cur.x - prev.x, cur.y - prev.y);
    const lenOut = Math.hypot(next.x - cur.x, next.y - cur.y);
    const r = Math.min(radius, lenIn / 2, lenOut / 2);
    if (r < 0.5) {
      // Degenerate corner — keep it sharp rather than emit a zero-length arc.
      d += ` L ${cur.x} ${cur.y}`;
      continue;
    }
    const ratioIn = (lenIn - r) / lenIn;
    const ratioOut = r / lenOut;
    const entry = {
      x: prev.x + (cur.x - prev.x) * ratioIn,
      y: prev.y + (cur.y - prev.y) * ratioIn,
    };
    const exit = {
      x: cur.x + (next.x - cur.x) * ratioOut,
      y: cur.y + (next.y - cur.y) * ratioOut,
    };
    d += ` L ${entry.x} ${entry.y} Q ${cur.x} ${cur.y} ${exit.x} ${exit.y}`;
  }
  const last = points[points.length - 1]!;
  d += ` L ${last.x} ${last.y}`;
  return d;
}

function pointToSegmentDistance(
  p: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  return Math.hypot(p.x - projX, p.y - projY);
}

/**
 * Given a click in flow coords, pick the polyline segment whose perpendicular
 * distance to the click is smallest. The segment index equals the position
 * at which a new waypoint should be spliced into the waypoints array:
 *   segment 0 (source→wp[0])          → splice at 0
 *   segment k (wp[k-1]→wp[k])         → splice at k
 *   segment n (wp[n-1]→target)        → splice at n
 */
export function findClosestSegmentIndex(
  p: { x: number; y: number },
  waypoints: ReadonlyArray<{ x: number; y: number }>,
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
): number {
  const anchors: Array<{ x: number; y: number }> = [
    { x: sourceX, y: sourceY },
    ...waypoints,
    { x: targetX, y: targetY },
  ];
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < anchors.length - 1; i += 1) {
    const a = anchors[i]!;
    const b = anchors[i + 1]!;
    const d = pointToSegmentDistance(p, a, b);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * Build a strictly-orthogonal SVG polyline through the ELK-computed bend
 * points.
 *
 * Coordinate-frame note: ELK reports section coordinates in the local space
 * of the edge's smallest-common-ancestor container. React Flow gives custom
 * edges `sourceX/sourceY` + `targetX/targetY` in the global viewport space.
 * For same-container edges these line up; for cross-container edges (and for
 * any sub-pixel handle/port-slot drift) ELK's last point won't match the
 * actual target handle.
 *
 * Strategy: translate every ELK bend by a single constant vector that snaps
 * the first bend to the source handle — interior bend-to-bend segments stay
 * orthogonal because ELK's ORTHOGONAL routing guarantees pairwise axis
 * alignment and a uniform translation preserves that. Then drop ELK's last
 * point (its port slot in the wrong frame) and bridge from the penultimate
 * bend to the real target handle along the axis ELK chose for the final
 * approach — that gives a guaranteed-orthogonal L-shape, never a diagonal.
 */
export function buildPolylinePath(
  bendPoints: ReadonlyArray<{ x: number; y: number }>,
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
): { path: string; labelX: number; labelY: number } {
  if (bendPoints.length < 2) {
    return { path: '', labelX: sourceX, labelY: sourceY };
  }
  const first = bendPoints[0]!;
  const dx = sourceX - first.x;
  const dy = sourceY - first.y;
  // Uniform translation: by construction, translated[0] === source handle.
  const translated = bendPoints.map((p) => ({ x: p.x + dx, y: p.y + dy }));

  // Drop ELK's last point (the local-frame port slot) and append an
  // orthogonal bridge to the real target handle.
  const points: Array<{ x: number; y: number }> = translated.slice(0, -1);
  const penult = points[points.length - 1]!;
  const elkLast = translated[translated.length - 1]!;
  // ELK's final-approach axis: same y → horizontal, otherwise → vertical.
  // Extend along that axis to the target, then turn 90° to hit it.
  if (approxEq(penult.y, elkLast.y)) {
    points.push({ x: targetX, y: penult.y });
  } else {
    points.push({ x: penult.x, y: targetY });
  }
  points.push({ x: targetX, y: targetY });

  // Drop zero-length segments produced when the corner happens to coincide
  // with a neighbour (target already on the approach axis).
  const deduped: Array<{ x: number; y: number }> = [points[0]!];
  for (let i = 1; i < points.length; i += 1) {
    const last = deduped[deduped.length - 1]!;
    const cur = points[i]!;
    if (!approxEq(last.x, cur.x) || !approxEq(last.y, cur.y)) {
      deduped.push(cur);
    }
  }

  // Label position: midpoint of the centre segment.
  const midIdx = Math.floor(deduped.length / 2);
  const prev = deduped[Math.max(midIdx - 1, 0)]!;
  const cur = deduped[midIdx]!;
  return {
    path: roundedPolylinePath(deduped, CORNER_RADIUS),
    labelX: (prev.x + cur.x) / 2,
    labelY: (prev.y + cur.y) / 2,
  };
}

/**
 * Build a straight-segment SVG path through user-positioned waypoints:
 * source → waypoint[0] → ... → waypoint[N-1] → target. The label sits at
 * the midpoint of the middle segment.
 */
export function buildWaypointPath(
  waypoints: ReadonlyArray<{ x: number; y: number }>,
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
): { path: string; labelX: number; labelY: number } {
  const points: Array<{ x: number; y: number }> = [
    { x: sourceX, y: sourceY },
    ...waypoints,
    { x: targetX, y: targetY },
  ];
  // User-positioned bends stay sharp: the gesture math in `SegmentDragHandles`
  // operates on the raw waypoint vertices, so the visible polyline must pass
  // through them exactly. (Corner rounding is applied to the deterministic
  // auto-route in `buildPolylinePath` only.)
  const segments: string[] = [`M ${points[0]!.x} ${points[0]!.y}`];
  for (let i = 1; i < points.length; i += 1) {
    segments.push(`L ${points[i]!.x} ${points[i]!.y}`);
  }
  // Label midpoint = centre of the centre segment.
  const midIdx = Math.floor(points.length / 2);
  const prev = points[Math.max(midIdx - 1, 0)]!;
  const cur = points[midIdx]!;
  return {
    path: segments.join(' '),
    labelX: (prev.x + cur.x) / 2,
    labelY: (prev.y + cur.y) / 2,
  };
}

/** Approximate where an edge's label lands, for collision GROUPING only. */
export interface LabelAnchor {
  id: string;
  x: number;
  y: number;
}

/**
 * Compute a per-edge VERTICAL offset that de-stacks connector labels which
 * would otherwise overlap (e.g. a fan of connectors leaving one part lands all
 * its labels on the same spot — see the Inverter screenshot).
 *
 * Algorithm: cluster anchors by geometric proximity (a single y-sorted greedy
 * pass), then within each cluster of size > 1 assign symmetric offsets by rank
 * (`(rank - (n-1)/2) * rowHeight`). Because the cluster is sorted by `y` and
 * the offset is monotonic in rank, the labels' vertical ORDER is preserved and
 * the gaps only ever WIDEN — so this can never CREATE a new within-cluster
 * overlap, it only separates labels that were stacked. Pure + deterministic so
 * it is unit-testable without the React Flow viewport.
 */
export function computeConnectionLabelOffsets(
  anchors: ReadonlyArray<LabelAnchor>,
  opts?: { xTol?: number; yTol?: number; rowHeight?: number },
): Map<string, number> {
  const xTol = opts?.xTol ?? 120;
  const yTol = opts?.yTol ?? 24;
  const rowHeight = opts?.rowHeight ?? 14;
  const offsets = new Map<string, number>();
  for (const a of anchors) offsets.set(a.id, 0);
  if (anchors.length < 2) return offsets;

  // Deterministic order: y, then x, then id.
  const sorted = [...anchors].sort(
    (a, b) => a.y - b.y || a.x - b.x || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );

  // Greedy clusters: a label joins the current cluster when it is within
  // tolerance of that cluster's anchor (its first, lowest-y member).
  let cluster: LabelAnchor[] = [];
  let refX = 0;
  let refY = 0;
  const flush = (): void => {
    if (cluster.length > 1) {
      const mid = (cluster.length - 1) / 2;
      cluster.forEach((a, i) => offsets.set(a.id, (i - mid) * rowHeight));
    }
    cluster = [];
  };
  for (const a of sorted) {
    if (cluster.length === 0) {
      cluster = [a];
      refX = a.x;
      refY = a.y;
      continue;
    }
    if (Math.abs(a.y - refY) <= yTol && Math.abs(a.x - refX) <= xTol) {
      cluster.push(a);
    } else {
      flush();
      cluster = [a];
      refX = a.x;
      refY = a.y;
    }
  }
  flush();
  return offsets;
}

export function ConnectionEdge(props: EdgeProps): JSX.Element {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    data,
    markerEnd,
    style,
  } = props;

  const edgeData = (data ?? {}) as ConnectionEdgeData;
  const elkBendPoints = (edgeData as { elkBendPoints?: ReadonlyArray<{ x: number; y: number }> })
    .elkBendPoints;
  const waypoints = (edgeData as { waypoints?: ReadonlyArray<{ x: number; y: number }> })
    .waypoints;
  const onWaypointChange = (edgeData as { onWaypointChange?: EdgeWaypointHandler })
    .onWaypointChange;
  const readOnly = (edgeData as { readOnly?: boolean }).readOnly === true;
  const rf = useReactFlow();

  // Segment-drag: a between-bend segment (the run between two consecutive
  // waypoints) is a translatable handle. Grabbing it locks to the segment's
  // perpendicular axis (horizontal segment → drag along Y only; vertical
  // segment → drag along X only) and moves BOTH endpoint waypoints by the
  // same delta. Port-adjacent segments (source→wp[0] and wp[N-1]→target)
  // are NOT draggable — their port endpoint is fixed, so translating them
  // would require auto-inserting new bends, which this gesture deliberately
  // avoids. Bends are created only via the ghost-midpoint handles (or, in a
  // follow-up, an edge context menu).

  let edgePath: string;
  let labelX: number;
  let labelY: number;
  if (waypoints !== undefined && waypoints.length > 0) {
    // User has placed at least one bend — render straight segments through
    // each waypoint. This wins over both ELK and smoothstep routing.
    const poly = buildWaypointPath(waypoints, sourceX, sourceY, targetX, targetY);
    edgePath = poly.path;
    labelX = poly.labelX;
    labelY = poly.labelY;
  } else if (elkBendPoints !== undefined && elkBendPoints.length >= 3) {
    // ELK has routed this edge around node bodies; render its polyline.
    // (`length >= 3` means at least one actual bend — a 2-point section is
    // just a straight line, smoothstep handles those fine and avoids a
    // sub-pixel disagreement between ELK's port slot and the React Flow
    // handle that would otherwise produce a tiny slant.)
    const poly = buildPolylinePath(elkBendPoints, sourceX, sourceY, targetX, targetY);
    edgePath = poly.path;
    labelX = poly.labelX;
    labelY = poly.labelY;
  } else {
    [edgePath, labelX, labelY] = getSmoothStepPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition,
    });
  }

  const kindIcon = edgeData.kind !== undefined ? KIND_ICON[edgeData.kind] : undefined;
  const labelText = edgeData.label;
  const hasMultiplicity =
    typeof edgeData.multiplicity === 'number' && edgeData.multiplicity > 1;
  const hasLabel = labelText !== undefined || kindIcon !== undefined || hasMultiplicity;
  const kindStyle = edgeData.kind !== undefined
    ? EDGE_KIND_STYLES[edgeData.kind]
    : DEFAULT_EDGE_STYLE;

  // De-stack overlapping connector labels: a fan of connectors leaving one
  // part lands all its midpoint labels on the same spot (and on the part's
  // edge). Nudge this label vertically by a collision-resolved offset. Grouping
  // uses each connection edge's approximate label anchor (the midpoint of its
  // two nodes' absolute centres); the per-cluster offset is monotonic in
  // y-rank so it only ever widens vertical gaps — it can never create a new
  // overlap. Snapshot read; recomputes whenever this edge re-renders.
  let labelDy = 0;
  if (hasLabel) {
    const centre = (nodeId: string): { x: number; y: number } | null => {
      const internal = rf.getInternalNode(nodeId) as
        | {
            internals?: { positionAbsolute?: { x: number; y: number } };
            measured?: { width?: number; height?: number };
          }
        | undefined;
      const pos = internal?.internals?.positionAbsolute;
      if (pos === undefined) return null;
      const w = internal?.measured?.width ?? 0;
      const h = internal?.measured?.height ?? 0;
      return { x: pos.x + w / 2, y: pos.y + h / 2 };
    };
    const anchors: LabelAnchor[] = [];
    for (const e of rf.getEdges()) {
      if (e.type !== 'connection') continue;
      const s = centre(e.source);
      const t = centre(e.target);
      if (s === null || t === null) continue;
      anchors.push({ id: e.id, x: (s.x + t.x) / 2, y: (s.y + t.y) / 2 });
    }
    labelDy = computeConnectionLabelOffsets(anchors).get(id) ?? 0;
  }

  const baseStyle: Record<string, string | number> = {
    stroke: kindStyle.stroke,
    strokeWidth: kindStyle.strokeWidth,
    // Round joins/caps soften the rounded-corner polyline and render the
    // information-flow dasharray as crisp dots rather than tiny rectangles.
    strokeLinejoin: 'round',
    strokeLinecap: 'round',
  };
  if (kindStyle.strokeDasharray !== undefined) {
    baseStyle.strokeDasharray = kindStyle.strokeDasharray;
  }

  // Changeset-preview override: when the edge carries a `data.changeKind`
  // tag, override stroke colour/width/dash so the user sees it as a
  // diff'd connection. 'unchanged' falls through to the per-kind style.
  const changeKind = edgeData.changeKind;
  if (
    changeKind === 'added' ||
    changeKind === 'updated' ||
    changeKind === 'removed'
  ) {
    const override = CHANGE_KIND_EDGE_STYLES[changeKind];
    baseStyle.stroke = override.stroke;
    baseStyle.strokeWidth = override.strokeWidth;
    if (override.strokeDasharray !== undefined) {
      baseStyle.strokeDasharray = override.strokeDasharray;
    } else {
      delete baseStyle.strokeDasharray;
    }
    if (changeKind === 'removed') {
      baseStyle.opacity = 0.7;
    }
  }

  // DLA emphasis overlay — an edge touching a de-emphasised node fades with it
  // (stamped `data.emphasisKind = 'deemphasize'` in buildReactFlowGraph). Layered
  // after the diff override so a diff'd edge that also dims keeps its recolour but
  // reads as secondary. Presentation-only.
  if ((edgeData as { emphasisKind?: unknown }).emphasisKind === 'deemphasize') {
    baseStyle.opacity = 0.3;
  }

  // Changeset-preview numbered badge: when the edge carries a
  // `data.changeIndex`, render a filled coloured chip at the label
  // midpoint with the 1-based ordinal so the user can match it to the
  // `[N]` prefix on the change-tree row.
  const changeIndexRaw = (edgeData as { changeIndex?: unknown }).changeIndex;
  const changeIndex =
    typeof changeIndexRaw === 'number' && Number.isFinite(changeIndexRaw) && changeIndexRaw >= 1
      ? Math.floor(changeIndexRaw)
      : undefined;
  const changeBadgePalette: Record<'added' | 'updated' | 'removed', string> = {
    added: '#10B981',
    updated: '#F59E0B',
    removed: '#F43F5E',
  };
  const badgeBg =
    changeIndex !== undefined && (changeKind === 'added' || changeKind === 'updated' || changeKind === 'removed')
      ? changeBadgePalette[changeKind]
      : '#6B6760';

  const interactive = !readOnly && onWaypointChange !== undefined;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{ ...baseStyle, ...(style ?? {}) }}
      />
      {interactive && onWaypointChange !== undefined && waypoints !== undefined && waypoints.length >= 2 && (
        <SegmentDragHandles
          edgeId={id}
          waypoints={waypoints}
          onChange={onWaypointChange}
        />
      )}
      {hasLabel && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              // `labelDy` is the collision-resolved vertical nudge that keeps
              // this label from stacking on neighbouring connector labels.
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY + labelDy}px)`,
              padding: '2px 7px',
              // Opaque chip + thin same-colour border so the label reads
              // cleanly when it sits over a node body, a port, or another
              // connector — i.e. it never visually merges into what's behind.
              background: 'var(--tessa-label-bg)',
              border: `1px solid ${baseStyle.stroke as string}`,
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
              whiteSpace: 'nowrap',
              // The flow-name label is painted in the CONNECTOR's colour —
              // `baseStyle.stroke` is the exact value the <BaseEdge> uses
              // (including the changeKind diff override), so the text always
              // matches the line it sits on.
              color: baseStyle.stroke as string,
              fontSize: 9.5,
              fontWeight: 600,
              borderRadius: 999,
              pointerEvents: 'none',
            }}
          >
            <ConnectionEdgeLabel edgeId={id} data={edgeData} />
          </div>
        </EdgeLabelRenderer>
      )}
      {changeIndex !== undefined && (
        <EdgeLabelRenderer>
          <div
            data-testid={`edge-change-badge-${id}`}
            data-change-index={changeIndex}
            aria-label={`Change ${changeIndex}`}
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY - 14}px)`,
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 9,
              background: badgeBg,
              color: '#FFFFFF',
              fontSize: 10,
              fontWeight: 700,
              lineHeight: '18px',
              textAlign: 'center',
              boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
              pointerEvents: 'none',
              userSelect: 'none',
              fontFamily:
                'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
            }}
          >
            {changeIndex}
          </div>
        </EdgeLabelRenderer>
      )}
      {!readOnly && onWaypointChange !== undefined && (
        <WaypointHandles
          edgeId={id}
          sourceX={sourceX}
          sourceY={sourceY}
          targetX={targetX}
          targetY={targetY}
          waypoints={waypoints ?? []}
          onChange={onWaypointChange}
        />
      )}
    </>
  );
}

/**
 * Per-segment transparent SVG `<line>` handles for the segments BETWEEN
 * two consecutive waypoints. Grabbing one locks the drag axis to the
 * segment's perpendicular (horizontal segments translate along Y only;
 * vertical segments translate along X only). Both endpoint waypoints
 * move together so the segment slides as a rigid bar.
 *
 * Hit-line attributes (`data-edge-id`, `data-segment-index`,
 * `data-segment-orientation`) are scraped by `web-e2e/tests/segment-drag.spec.ts`
 * — keep them stable.
 */
export function SegmentDragHandles(props: {
  edgeId: string;
  waypoints: ReadonlyArray<{ x: number; y: number }>;
  onChange: EdgeWaypointHandler;
}): JSX.Element | null {
  const { edgeId, waypoints, onChange } = props;
  const { screenToFlowPosition } = useReactFlow();
  const [dragState, setDragState] = useState<
    | null
    | {
        index: number;
        axis: 'horizontal' | 'vertical';
        snapshot: ReadonlyArray<{ x: number; y: number }>;
        originFlow: { x: number; y: number };
      }
  >(null);
  const dragStateRef = useRef(dragState);
  useEffect(() => {
    dragStateRef.current = dragState;
  }, [dragState]);
  useEffect(() => {
    if (dragState === null) return undefined;
    const onPointerMove = (ev: PointerEvent): void => {
      const cur = dragStateRef.current;
      if (cur === null) return;
      const flow = screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
      const next = cur.snapshot.map((w) => ({ x: w.x, y: w.y }));
      const a = next[cur.index - 1]!;
      const b = next[cur.index]!;
      if (cur.axis === 'horizontal') {
        const dy = flow.y - cur.originFlow.y;
        a.y = cur.snapshot[cur.index - 1]!.y + dy;
        b.y = cur.snapshot[cur.index]!.y + dy;
      } else {
        const dx = flow.x - cur.originFlow.x;
        a.x = cur.snapshot[cur.index - 1]!.x + dx;
        b.x = cur.snapshot[cur.index]!.x + dx;
      }
      onChange(edgeId, next);
    };
    const onPointerUp = (): void => {
      setDragState(null);
    };
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerUp);
    return (): void => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerUp);
    };
  }, [dragState, edgeId, onChange, screenToFlowPosition]);

  if (waypoints.length < 2) return null;

  type Handle = {
    index: number;
    axis: 'horizontal' | 'vertical' | 'diagonal';
    a: { x: number; y: number };
    b: { x: number; y: number };
  };
  const handles: Handle[] = [];
  for (let k = 1; k < waypoints.length; k += 1) {
    const a = waypoints[k - 1]!;
    const b = waypoints[k]!;
    const segDx = Math.abs(b.x - a.x);
    const segDy = Math.abs(b.y - a.y);
    const axis: 'horizontal' | 'vertical' | 'diagonal' =
      segDx > segDy + ORTHO_EPSILON
        ? 'horizontal'
        : segDy > segDx + ORTHO_EPSILON
        ? 'vertical'
        : 'diagonal';
    handles.push({ index: k, axis, a, b });
  }

  return (
    <>
      {handles.map((h) => {
        const draggable = h.axis !== 'diagonal';
        return (
          <line
            key={`seg-${edgeId}-${String(h.index)}`}
            x1={h.a.x}
            y1={h.a.y}
            x2={h.b.x}
            y2={h.b.y}
            stroke="transparent"
            strokeWidth={14}
            data-edge-id={edgeId}
            data-segment-index={h.index}
            data-segment-orientation={h.axis}
            data-testid={`edge-segment-${edgeId}-${String(h.index)}`}
            style={{
              cursor: draggable ? (h.axis === 'horizontal' ? 'ns-resize' : 'ew-resize') : 'default',
              pointerEvents: draggable ? 'stroke' : 'none',
            }}
            onPointerDown={
              draggable
                ? (ev: ReactPointerEvent<SVGLineElement>): void => {
                    ev.stopPropagation();
                    ev.preventDefault();
                    const flow = screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
                    setDragState({
                      index: h.index,
                      axis: h.axis as 'horizontal' | 'vertical',
                      snapshot: waypoints.map((w) => ({ x: w.x, y: w.y })),
                      originFlow: flow,
                    });
                  }
                : undefined
            }
          />
        );
      })}
    </>
  );
}

/**
 * Renders a draggable circle at every persisted waypoint plus a "ghost"
 * midpoint handle at the centre of every segment. Dragging the ghost
 * materialises a new waypoint; dragging an existing waypoint moves it.
 * Double-click an existing waypoint to remove it.
 *
 * Coordinate conversion uses `useReactFlow().screenToFlowPosition` so the
 * drag tracks correctly under pan / zoom.
 */
function WaypointHandles(props: {
  edgeId: string;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  waypoints: ReadonlyArray<{ x: number; y: number }>;
  onChange: EdgeWaypointHandler;
}): JSX.Element {
  const { edgeId, sourceX, sourceY, targetX, targetY, waypoints, onChange } = props;
  const { screenToFlowPosition } = useReactFlow();
  // `dragState` carries the index being dragged AND a snapshot of the
  // waypoint list at drag-start so we can splice ghost handles in without
  // losing the original mouse-down position.
  const [dragState, setDragState] = useState<
    | null
    | {
        kind: 'existing' | 'ghost';
        index: number;
        snapshot: ReadonlyArray<{ x: number; y: number }>;
      }
  >(null);
  const dragStateRef = useRef(dragState);
  useEffect(() => {
    dragStateRef.current = dragState;
  }, [dragState]);

  // Document-level pointer move/up handlers so the drag survives leaving the
  // SVG element. Attached only while a drag is in flight.
  useEffect(() => {
    if (dragState === null) return undefined;
    const onPointerMove = (ev: PointerEvent): void => {
      const cur = dragStateRef.current;
      if (cur === null) return;
      const flow = screenToFlowPosition({ x: ev.clientX, y: ev.clientY });
      const next = [...cur.snapshot];
      if (cur.kind === 'ghost') {
        next.splice(cur.index, 0, { x: flow.x, y: flow.y });
        // Promote the ghost drag to an existing-waypoint drag now that the
        // point has materialised — the snapshot baseline shifts too.
        dragStateRef.current = {
          kind: 'existing',
          index: cur.index,
          snapshot: next,
        };
        setDragState(dragStateRef.current);
      } else {
        next[cur.index] = { x: flow.x, y: flow.y };
      }
      onChange(edgeId, next);
    };
    const onPointerUp = (): void => {
      setDragState(null);
    };
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerUp);
    return (): void => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerUp);
    };
  }, [dragState, edgeId, onChange, screenToFlowPosition]);

  const startDragExisting = useCallback(
    (idx: number) => (ev: ReactPointerEvent<HTMLDivElement>): void => {
      ev.stopPropagation();
      ev.preventDefault();
      setDragState({ kind: 'existing', index: idx, snapshot: [...waypoints] });
    },
    [waypoints],
  );
  const startDragGhost = useCallback(
    (insertIdx: number) => (ev: ReactPointerEvent<HTMLDivElement>): void => {
      ev.stopPropagation();
      ev.preventDefault();
      setDragState({ kind: 'ghost', index: insertIdx, snapshot: [...waypoints] });
    },
    [waypoints],
  );
  const removeWaypoint = useCallback(
    (idx: number) => (ev: ReactMouseEvent<HTMLDivElement>): void => {
      ev.stopPropagation();
      ev.preventDefault();
      const next = [...waypoints];
      next.splice(idx, 1);
      onChange(edgeId, next);
    },
    [edgeId, onChange, waypoints],
  );

  // Ghost handle = midpoint of each segment between consecutive anchor
  // points (source, waypoints..., target).
  const anchors: Array<{ x: number; y: number }> = [
    { x: sourceX, y: sourceY },
    ...waypoints,
    { x: targetX, y: targetY },
  ];
  const ghosts: Array<{ x: number; y: number; insertAt: number }> = [];
  for (let i = 0; i < anchors.length - 1; i += 1) {
    const a = anchors[i]!;
    const b = anchors[i + 1]!;
    ghosts.push({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, insertAt: i });
  }

  return (
    <EdgeLabelRenderer>
      {ghosts.map((g) => (
        <div
          key={`ghost-${edgeId}-${String(g.insertAt)}`}
          data-testid={`edge-waypoint-ghost-${edgeId}-${String(g.insertAt)}`}
          data-edge-id={edgeId}
          data-waypoint-ghost-index={g.insertAt}
          role="button"
          aria-label="Add edge bend"
          onPointerDown={startDragGhost(g.insertAt)}
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${g.x}px, ${g.y}px)`,
            width: 10,
            height: 10,
            borderRadius: 10,
            background: 'transparent',
            border: '1.5px dashed var(--tessa-fg)',
            opacity: 0.7,
            cursor: 'crosshair',
            pointerEvents: 'all',
            touchAction: 'none',
          }}
        />
      ))}
      {waypoints.map((w, idx) => (
        <div
          key={`wp-${edgeId}-${String(idx)}`}
          data-testid={`edge-waypoint-${edgeId}-${String(idx)}`}
          data-edge-id={edgeId}
          data-waypoint-index={idx}
          role="button"
          aria-label="Drag to move bend; double-click to remove"
          onPointerDown={startDragExisting(idx)}
          onDoubleClick={removeWaypoint(idx)}
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${w.x}px, ${w.y}px)`,
            width: 10,
            height: 10,
            borderRadius: 10,
            background: 'var(--tessa-bg)',
            border: '2px solid var(--tessa-fg)',
            cursor: 'grab',
            pointerEvents: 'all',
            touchAction: 'none',
          }}
        />
      ))}
    </EdgeLabelRenderer>
  );
}

export default ConnectionEdge;
